import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, subDays, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Filter, Plus, Trash2, Edit, Eye, FileText, Wallet, Package, Scissors, 
  AlertTriangle, BarChart3, Database, RefreshCw, TrendingUp, Users, Activity as ActivityIcon, Clock,
  LogIn, LogOut
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { ActivityLog } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const ActivityClientes: React.FC = () => {
  const { isAdmin } = useAuth();

  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<{ id: string; email: string; role: string }[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'analytics'>('table');
  const [usersError, setUsersError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  
  // Filters (role is fixed to Cliente)
  const [filters, setFilters] = useState({
    userId: 'all',
    period: 'month',
    startDate: '',
    endDate: '',
    searchQuery: '',
  });

  // Guard: only administrators
  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <AlertTriangle className="h-10 w-10 text-amber-600 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Acceso restringido</p>
            <p className="text-sm text-gray-500">Esta página solo está disponible para administradores.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fetch cliente activity logs
  useEffect(() => {
    const fetchClienteActivityLogs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('cliente_activity_logs')
          .select(`
            *,
            clients(
              id,
              name,
              email
            )
          `)
          .order('created_at', { ascending: false })
          .limit(1000);
        if (error) throw error;
        setActivityLogs(data || []);
      } catch (err: any) {
        console.error('Error fetching cliente activity logs:', err);
        setError(err.message || 'Error al cargar los registros de actividad de clientes');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClienteActivityLogs();
  }, []);

  // Fetch clientes (for mapping emails y nombres)
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const { data, error } = await supabase.from('clients').select('id, name, business_name, email');
        if (error) throw error;
        const formatted = (data || []).map((c: any) => ({ id: c.id, email: c.email, name: c.name }));
        setUsers(formatted);
        setUsersError(null);
      } catch (err: any) {
        console.warn('Error fetching clientes:', err);
        const unique = new Set<string>();
        activityLogs.forEach(l => l.cliente_id && unique.add(l.cliente_id));
        const minimal = Array.from(unique).map(id => ({ id, email: `cliente.${id.slice(0,8)}@empresa.com`, name: 'Cliente' }));
        setUsers(minimal);
        setUsersError('Información de clientes limitada. Se muestran IDs extraídos de la actividad.');
      }
    };
    fetchClientes();
  }, [activityLogs]);

  // Fechas rápidas
  useEffect(() => {
    if (filters.period === 'custom') return;
    const now = new Date();
    let startDate = '';
    switch (filters.period) {
      case 'today': startDate = format(now, 'yyyy-MM-dd'); break;
      case '7days': startDate = format(subDays(now, 7), 'yyyy-MM-dd'); break;
      case '30days': startDate = format(subDays(now, 30), 'yyyy-MM-dd'); break;
      case 'month': startDate = format(startOfMonth(now), 'yyyy-MM-dd'); break;
      case 'quarter': startDate = format(startOfQuarter(now), 'yyyy-MM-dd'); break;
      case 'year': startDate = format(startOfYear(now), 'yyyy-MM-dd'); break;
      default: startDate = ''; break;
    }
    setFilters(prev => ({ ...prev, startDate, endDate: startDate ? format(now, 'yyyy-MM-dd') : '' }));
  }, [filters.period]);

  // Filtrar actividades de clientes
  const filteredLogs = useMemo(() => {
    const filtered = (activityLogs || []).filter(log => {
      // Aplicar filtros
      if (filters.userId !== 'all' && log.cliente_id !== filters.userId) return false;

      if (filters.startDate && filters.endDate) {
        const logDate = parseISO(log.created_at);
        const startDate = new Date(filters.startDate + 'T00:00:00');
        const endDate = new Date(filters.endDate + 'T23:59:59');
        if (logDate < startDate || logDate > endDate) return false;
      }

      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        const clienteEmail = (log.clients?.email || '').toLowerCase();
        const clienteName = (log.clients?.name || '').toLowerCase();
        return (
          log.action.toLowerCase().includes(q) ||
          log.details.toLowerCase().includes(q) ||
          clienteEmail.includes(q) ||
          clienteName.includes(q)
        );
      }

      return true;
    });
    return filtered;
  }, [activityLogs, filters]);

  // Paginación
  const totalPages = Math.ceil((filteredLogs?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = (filteredLogs || []).slice(startIndex, endIndex);

  useEffect(() => { setCurrentPage(1); }, [filters]);

  const getClienteInfo = (log: any): { email: string; name: string } => {
    return {
      email: log.clients?.email || 'cliente@empresa.com',
      name: log.clients?.name || log.clients?.business_name || 'Cliente'
    };
  };

  const translateDbTerms = (text: string): string => {
    const map: { [k: string]: string } = {
      sample_tasks: 'tareas de muestra',
      production_tasks: 'tareas de producción',
      suppliers: 'proveedores',
      clients: 'clientes',
      samples: 'muestras',
      productions: 'producciones',
      express_samples: 'muestras express',
      express_orders: 'pedidos express',
      invoices: 'facturas',
      payments: 'pagos',
      expenses: 'gastos',
      logistics_trips: 'viajes logísticos',
      crm_leads: 'prospectos',
      crm_reminders: 'recordatorios',
      users: 'usuarios',
      create: 'crear', created: 'creado', update: 'actualizar', updated: 'actualizado', delete: 'eliminar', deleted: 'eliminado', view: 'ver', viewed: 'visto', login: 'inicio de sesión', logout: 'cierre de sesión',
      task: 'tarea', tasks: 'tareas', sample: 'muestra', production: 'producción', client: 'cliente', invoice: 'factura', payment: 'pago', expense: 'gasto'
    };
    let out = text;
    Object.keys(map).forEach(k => { out = out.replace(new RegExp(`\\b${k}\\b`, 'gi'), map[k]); });
    return out;
  };

  // Estadísticas para actividades de clientes
  const statistics = useMemo(() => {
    if (!filteredLogs || filteredLogs.length === 0) {
      return { totalActions: 0, uniqueClients: 0, actionsByType: {}, actionsByClient: {}, actionsByDay: {}, averageActionsPerDay: 0, totalDays: 0 };
    }
    const actionsByType: any = {};
    const actionsByClient: any = {};
    const actionsByDay: any = {};
    const uniqueClients = new Set<string>();
    const uniqueDays = new Set<string>();
    filteredLogs.forEach(log => {
      actionsByType[log.action_type] = (actionsByType[log.action_type] || 0) + 1;
      actionsByClient[log.cliente_id] = (actionsByClient[log.cliente_id] || 0) + 1;
      uniqueClients.add(log.cliente_id);
      const day = format(parseISO(log.created_at), 'yyyy-MM-dd');
      actionsByDay[day] = (actionsByDay[day] || 0) + 1;
      uniqueDays.add(day);
    });
    return {
      totalActions: filteredLogs.length,
      uniqueClients: uniqueClients.size,
      actionsByType,
      actionsByClient,
      actionsByDay,
      averageActionsPerDay: uniqueDays.size > 0 ? Math.round(filteredLogs.length / uniqueDays.size * 10) / 10 : 0,
      totalDays: uniqueDays.size
    };
  }, [filteredLogs]);

  const logsGroupedByDay = useMemo(() => {
    if (!paginatedLogs || paginatedLogs.length === 0) return [] as any[];
    const grouped: any = {};
    paginatedLogs.forEach(l => {
      const d = format(parseISO(l.created_at), 'yyyy-MM-dd');
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(l);
    });
    return Object.keys(grouped).sort((a,b) => b.localeCompare(a)).map(d => ({ date: d, logs: grouped[d].sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) }));
  }, [paginatedLogs]);

  const getActionIcon = (log: ActivityLog) => {
    const iconProps = { className: 'h-5 w-5' } as any;
    if (log.action_type === 'create') return <Plus {...iconProps} />;
    if (log.action_type === 'update') return <Edit {...iconProps} />;
    if (log.action_type === 'delete') return <Trash2 {...iconProps} />;
    if (log.action_type === 'view') return <Eye {...iconProps} />;
    switch (log.entity_type) {
      case 'invoice': return <FileText {...iconProps} />;
      case 'payment': return <Wallet {...iconProps} />;
      case 'express':
      case 'express_stock': return <Package {...iconProps} />;
      case 'sample':
      case 'express_sample': return <Scissors {...iconProps} />;
      default: return <AlertTriangle {...iconProps} />;
    }
  };

  const getActionColorClass = (log: ActivityLog): string => {
    if (log.action_type === 'login') return 'bg-emerald-100 text-emerald-800';
    if (log.action_type === 'logout') return 'bg-amber-100 text-amber-800';
    if (log.action_type === 'create') return 'bg-green-100 text-green-800';
    if (log.action_type === 'delete') return 'bg-red-100 text-red-800';
    if (log.action_type === 'update') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const resetFilters = () => {
    setFilters({ userId: 'all', period: 'month', startDate: '', endDate: '', searchQuery: '' });
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <ActivityIcon className="mr-2 h-6 w-6 text-blue-600" />
          Actividad de Clientes
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" icon={<RefreshCw className="h-4 w-4" />} onClick={() => window.location.reload()} size="sm">Actualizar</Button>
          <Button variant="outline" icon={<Filter className="h-4 w-4" />} onClick={() => setShowFilters(!showFilters)} size="sm">Filtros</Button>
          <Button variant="outline" icon={<Filter className="h-4 w-4" />} onClick={resetFilters} size="sm">Limpiar</Button>
        </div>
      </div>

      {/* Filters para actividades relacionadas con clientes */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Período"
                value={filters.period}
                onChange={(value) => setFilters(prev => ({ ...prev, period: value }))}
                options={[{value:'today',label:'Hoy'},{value:'7days',label:'Últimos 7 días'},{value:'30days',label:'Últimos 30 días'},{value:'month',label:'Este mes'},{value:'quarter',label:'Este trimestre'},{value:'year',label:'Este año'},{value:'custom',label:'Personalizado'}]}
              />
              <Select
                label="Cliente"
                value={filters.userId}
                onChange={(value) => setFilters(prev => ({ ...prev, userId: value }))}
                options={[{ value: 'all', label: 'Todos los clientes' }, ...(users || []).map(u => ({ value: u.id, label: `${u.name} (${u.email})` }))]}
              />
              <Input
                label="Buscar"
                placeholder="Acción, detalle o email"
                value={filters.searchQuery}
                onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
              />
            </div>
            {filters.period === 'custom' && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Input label="Desde" type="date" value={filters.startDate} onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))} />
                <Input label="Hasta" type="date" value={filters.endDate} onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-1">
        <div className="flex">
          <button onClick={() => setViewMode('table')} className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md ${viewMode==='table'?'bg-gray-900 text-white':'text-gray-500 hover:text-gray-700'}`}>
            <Database className="h-4 w-4 mr-2" /> Tabla Detallada
          </button>
          <button onClick={() => setViewMode('analytics')} className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md ${viewMode==='analytics'?'bg-gray-900 text-white':'text-gray-500 hover:text-gray-700'}`}>
            <BarChart3 className="h-4 w-4 mr-2" /> Estadísticas
          </button>
        </div>
      </div>

      {/* Contenido */}
      {viewMode === 'table' && (
        <Card>
          <CardHeader title="Actividades de Clientes en su Portal" />
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 p-4 rounded-md text-red-700">{error}</div>
            ) : (filteredLogs?.length || 0) === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Database className="h-16 w-16 mx-auto mb-6 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Actividad de Clientes</h3>
                <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                  No se encontraron actividades de clientes en el período seleccionado.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto">
                  <p className="text-sm text-blue-800">
                    <strong>Nota:</strong> Esta página muestra las actividades que realizan los clientes 
                    en su portal de clientes (login, logout, visualización de pedidos, muestras, etc.).
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="bg-white">
                      {logsGroupedByDay.map((dayGroup) => (
                        <React.Fragment key={dayGroup.date}>
                          <tr className="bg-gray-100">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="flex items-center">
                                <div className="flex-1 border-t border-gray-300"></div>
                                <div className="px-3">
                                  <span className="text-sm font-semibold text-gray-700">
                                    {format(parseISO(dayGroup.date), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es })}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500">({dayGroup.logs.length} acciones)</span>
                                </div>
                                <div className="flex-1 border-t border-gray-300"></div>
                              </div>
                            </td>
                          </tr>
                          {dayGroup.logs.map((log: any) => {
                            const clienteInfo = getClienteInfo(log);
                            return (
                              <tr key={log.id} className="hover:bg-gray-50 border-b border-gray-100">
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className="text-xs font-medium text-gray-900">{clienteInfo.name}</div>
                                  <div className="text-xs text-gray-500">{clienteInfo.email}</div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className="px-1.5 py-0.5 inline-flex text-xs leading-4 font-medium rounded-full bg-blue-100 text-blue-800">Cliente</span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className={`px-1.5 py-0.5 inline-flex items-center text-xs leading-4 font-medium rounded-full ${getActionColorClass(log)}`}>
                                    <span className="mr-1">{getActionIcon(log)}</span>
                                    {translateDbTerms(log.action)}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="text-xs text-gray-900 max-w-xs truncate">{translateDbTerms(log.details)}</div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 font-mono">
                                  {format(parseISO(log.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
                      <div>
                        <p className="text-sm text-gray-700">
                          Mostrando <span className="font-medium">{startIndex + 1}</span> a <span className="font-medium">{Math.min(endIndex, filteredLogs?.length || 0)}</span> de <span className="font-medium">{filteredLogs?.length || 0}</span> resultados
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <Button variant="outline" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md">Anterior</Button>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            if (page > totalPages) return null;
                            return (
                              <Button key={page} variant={currentPage === page ? 'primary' : 'outline'} onClick={() => setCurrentPage(page)} className="relative inline-flex items-center px-3 py-2">{page}</Button>
                            );
                          })}
                          <Button variant="outline" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md">Siguiente</Button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {viewMode === 'analytics' && (
        <div className="space-y-6">
          {/* Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card><CardContent className="p-4"><div className="flex items-center"><div className="p-2 bg-blue-100 rounded-lg"><ActivityIcon className="h-5 w-5 text-blue-600" /></div><div className="ml-3"><p className="text-sm font-medium text-gray-500">Total Acciones</p><p className="text-2xl font-bold text-gray-900">{statistics.totalActions}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center"><div className="p-2 bg-green-100 rounded-lg"><Users className="h-5 w-5 text-green-600" /></div><div className="ml-3"><p className="text-sm font-medium text-gray-500">Clientes Activos</p><p className="text-2xl font-bold text-gray-900">{statistics.uniqueClients}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center"><div className="p-2 bg-purple-100 rounded-lg"><TrendingUp className="h-5 w-5 text-purple-600" /></div><div className="ml-3"><p className="text-sm font-medium text-gray-500">Promedio/Día</p><p className="text-2xl font-bold text-gray-900">{statistics.averageActionsPerDay}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center"><div className="p-2 bg-orange-100 rounded-lg"><Clock className="h-5 w-5 text-orange-600" /></div><div className="ml-3"><p className="text-sm font-medium text-gray-500">Días Activos</p><p className="text-2xl font-bold text-gray-900">{statistics.totalDays}</p></div></div></CardContent></Card>
          </div>

          {/* Detalle */}
          <Card>
            <CardHeader title="Acciones por Tipo" />
            <CardContent>
              <div className="space-y-3">
                {Object.entries(statistics.actionsByType).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${type==='create'?'bg-green-500':type==='update'?'bg-blue-500':type==='delete'?'bg-red-500':'bg-gray-500'}`}></div>
                      <span className="text-sm font-medium text-gray-900 capitalize">{translateDbTerms(type)}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{count as number}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ActivityClientes;
